<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import Chart from 'chart.js/auto'

const emit = defineEmits(['update:activeMonth'])

const props = defineProps({
  // [{code, classification_month, status, stats: {ClassName: {area_ha, percentage, class_id}}}]
  months: { type: Array, required: true },
  // Ordered list of class names from the model — drives stack order so all
  // months share the same legend; each month adds 0 for missing classes.
  classNames: { type: Array, default: () => [] },
  // Map of class name → hex color (sourced from model.metadata.classes).
  // Required for cross-chart consistency (bar chart, donut, map all match).
  classColors: { type: Object, default: () => ({}) },
  // AOI polygon area in hectares. Used as the y-axis max so bar heights are
  // visually anchored to the polygon's true area instead of autoscaling to
  // the largest stacked total (which can drift with rasterization rounding).
  aoiAreaHa: { type: Number, default: null },
  activeMonthCode: { type: String, default: null },
})

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatMonthLabel = (key) => {
  if (!key) return ''
  const parts = String(key).split('_')
  if (parts.length !== 2) return key
  const year = Number(parts[0])
  const month = Number(parts[1])
  if (!year || !month) return key
  return `${MONTH_LABELS[month - 1]} ${year}`
}

const monthsSorted = computed(() =>
  [...props.months]
    .filter(m => m.classification_month)
    .sort((a, b) => String(a.classification_month).localeCompare(String(b.classification_month)))
)

// Build the union of class names across all months (preserves model order if supplied).
const classOrder = computed(() => {
  if (props.classNames.length) return props.classNames
  const set = new Set()
  for (const m of monthsSorted.value) {
    const stats = m.stats || {}
    for (const name of Object.keys(stats)) set.add(name)
  }
  return [...set]
})

const datasets = computed(() => classOrder.value.map((name) => ({
  label: name,
  data: monthsSorted.value.map((m) => {
    const v = m.stats?.[name]
    return v ? Number(v.area_ha || 0) : 0
  }),
  backgroundColor: props.classColors?.[name] || '#9ca3af',
  borderWidth: 0,
  stack: 'lulc',
})))

const chartId = `lulc-stacked-${Math.random().toString(36).slice(2, 9)}`
const chartRef = ref(null)
let chartInstance = null

const buildChart = () => {
  if (!chartRef.value) return
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
  const ctx = chartRef.value.getContext('2d')
  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: monthsSorted.value.map(m => formatMonthLabel(m.classification_month)),
      datasets: datasets.value,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      onHover: (_event, elements) => {
        if (elements.length > 0) {
          const idx = elements[0].index
          const month = monthsSorted.value[idx]
          if (month) emit('update:activeMonth', month)
        }
      },
      scales: {
        x: {
          stacked: true,
          ticks: { autoSkip: true, maxRotation: 45, minRotation: 0 },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ...(props.aoiAreaHa ? { max: props.aoiAreaHa } : {}),
          title: { display: true, text: 'Area (ha)' },
        },
      },
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
        tooltip: { mode: 'index', intersect: false },
      },
    },
  })
}

onMounted(buildChart)
onUnmounted(() => {
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
})

watch(
  () => [monthsSorted.value, datasets.value],
  () => buildChart(),
  { deep: true },
)
</script>

<template>
  <div class="relative" style="height: 220px">
    <canvas :id="chartId" ref="chartRef"></canvas>
  </div>
</template>
